import type { DepartmentRef, PersonalMemberLimitDetails, SpaceKind, SpaceRestriction } from "@/api";

/**
 * What a license takes away from a project or a dataspace, and what is said about it: the pure part
 * of `SpaceRestrictedState`, `SpaceRestrictionBanner`, the badges of the lists and the words of the
 * refusals, so the rules are held in one place and tested without a component.
 *
 * The server decides every one of these (`access::restriction`) and the client only says it. Reasons
 * are the server's strings; one this client does not know yet is said in general words (`other`),
 * never shown raw.
 */

/** The two things a restriction can take away: the Designer of a project, or the space itself. */
export type RestrictionPart = "designer" | "locked";

/** What is the matter with a space right now: which part, and why. */
export interface ActiveRestriction {
	part: RestrictionPart;
	reason: string;
}

/** The reasons this client has words for, per part. Anything else is `other`. */
const KNOWN_REASONS: Record<RestrictionPart, readonly string[]> = {
	designer: ["server", "department"],
	locked: ["department_unlicensed", "personal_spaces_disabled", "personal_member_limit"],
};

/** `reason` when there are words for it under `part`, else `other`. */
export function knownReason(part: RestrictionPart, reason: string | null | undefined): string {
	return reason && KNOWN_REASONS[part].includes(reason) ? reason : "other";
}

/**
 * The one restriction a space is shown with. A closed Designer is the larger fact — nothing of the
 * project opens — so it wins over a lock; a dataspace has no Designer. The space's own `restriction`
 * decides, and `refusal` — what the server just refused with, for a session that has not read the
 * space again yet — stands in when it says nothing.
 */
export function activeRestriction(
	kind: SpaceKind,
	restriction: SpaceRestriction | null | undefined,
	refusal?: { code: string; reason: string } | null,
): ActiveRestriction | null {
	if (kind === "project" && restriction?.designer) {
		return { part: "designer", reason: knownReason("designer", restriction.designer.reason) };
	}
	if (restriction?.locked) {
		return { part: "locked", reason: knownReason("locked", restriction.locked.reason) };
	}
	if (restriction) return null;
	if (kind === "project" && refusal?.code === "designer_unavailable") {
		return { part: "designer", reason: knownReason("designer", refusal.reason) };
	}
	if (refusal?.code === "space_locked") {
		return { part: "locked", reason: knownReason("locked", refusal.reason) };
	}
	return null;
}

/** Whether anything is taken away: what a list marks and a page that stays usable says. */
export function isRestricted(restriction: SpaceRestriction | null | undefined): boolean {
	return !!restriction && (!!restriction.designer || !!restriction.locked);
}

// ── What is said ──────────────────────────────────────────────────────────────

/** What a page needs to put numbers and names into the words — all of it optional. */
export interface RestrictionContext {
	/** The department the space is in; `null` for a personal one. */
	department?: DepartmentRef | null;
	/** How many explicit members the space has. */
	members?: number | null;
	/** The license's limit on a personal space's members. */
	limit?: number | null;
}

/** An i18n key, its parameters, and the count that picks its plural form when it has one. */
export interface Words {
	key: string;
	params?: Record<string, string | number>;
	plural?: number;
}

/** The heading of the state or the banner: what the restriction does to the space. */
export function restrictionTitle(kind: SpaceKind, active: ActiveRestriction): Words {
	if (active.part === "designer") return { key: "spaceRestriction.title.designer" };
	return { key: `spaceRestriction.title.${kind}` };
}

/**
 * What happened and how to fix it. A reason that is about a department says which when it is known,
 * and a personal space over its limit says how far over when both numbers are.
 */
export function restrictionNote(
	active: ActiveRestriction,
	context: RestrictionContext = {},
): Words {
	const base = `spaceRestriction.note.${active.part}.${active.reason}`;
	const department = context.department?.name;
	if (active.reason === "personal_member_limit") {
		return typeof context.members === "number" && typeof context.limit === "number"
			? { key: `${base}Counted`, params: { members: context.members, limit: context.limit } }
			: { key: base };
	}
	if (
		department &&
		(active.reason === "department" || active.reason === "department_unlicensed")
	) {
		return { key: `${base}Named`, params: { department } };
	}
	return { key: base };
}

/** The line a closed dataspace adds: what is running keeps running, and what winds it down is open. */
export const RUNNING_NOTE: Words = { key: "spaceRestriction.running" };

/** The small badge a list draws for a restricted space, and what it says on hover. */
export interface RestrictionBadge {
	/** `designer` closed, `project` read-only or `dataspace` locked. */
	label: Words;
	/** The reason, in a line — the badge's title. */
	hint: Words;
	part: RestrictionPart;
}

/** The badge for a space in a list, or `null` for one nothing is taken away from. */
export function restrictionBadge(
	kind: SpaceKind,
	restriction: SpaceRestriction | null | undefined,
): RestrictionBadge | null {
	const active = activeRestriction(kind, restriction);
	if (!active) return null;
	return {
		part: active.part,
		label: {
			key:
				active.part === "designer"
					? "spaceRestriction.badge.designer"
					: `spaceRestriction.badge.${kind}`,
		},
		hint: { key: `spaceRestriction.short.${active.part}.${active.reason}` },
	};
}

// ── Where it is fixed ─────────────────────────────────────────────────────────

/** The entitlements a space's own role holds for what fixes a restriction: its members and its delete — the right to give it away. */
export const SPACE_ENTITLEMENTS: Record<SpaceKind, { manageMembers: string; delete: string }> = {
	project: { manageMembers: "members:manage", delete: "project:delete" },
	dataspace: { manageMembers: "dataspace:members:manage", delete: "dataspace:delete" },
};

/** What the viewer may do about it — the page only offers a link that leads somewhere they may act. */
export interface FixRights {
	/** They may manage the space's members. */
	manageMembers: boolean;
	/** They may move the space: the right to delete it, which is the right to give it away. */
	move: boolean;
	/** They hold `department:license:manage` in the space's department. */
	manageDepartmentLicense: boolean;
	/** `system:manage_server`: the control center's license settings. */
	manageServer: boolean;
	/** `system:manage_departments`: the control center's departments. */
	manageDepartments: boolean;
}

/** A link to where the cause is fixed. `href` is an in-app path; the label is words. */
export interface Fix {
	href: string;
	label: Words;
}

/** The settings of a space, at one of its sections: where its members and its department are. */
export function settingsPath(
	kind: SpaceKind,
	id: string,
	section: "members" | "department",
): string {
	return kind === "project"
		? `/designer/${encodeURIComponent(id)}/home?section=${section}`
		: `/operator/${encodeURIComponent(id)}/settings/${section}`;
}

/**
 * The page where the cause is fixed, for a viewer who may act there, else `null` (the note says
 * what to do and whom to ask; a link that only ends in a refusal is not offered).
 *
 *  - too many members → the space's members, for whoever manages them;
 *  - no personal spaces → its department section, for whoever may move it;
 *  - a department's license → the department's own page, for whoever manages its license, and the
 *    control center's list of departments for a server administrator who may open it;
 *  - the server's license → the control center's license section, for whoever manages the server.
 */
export function restrictionFix(
	kind: SpaceKind,
	spaceId: string,
	active: ActiveRestriction,
	rights: FixRights,
	department?: DepartmentRef | null,
): Fix | null {
	const departmentFix = (): Fix | null => {
		if (department && rights.manageDepartmentLicense) {
			return {
				href: `/departments/${encodeURIComponent(department.id)}`,
				label: {
					key: "spaceRestriction.fix.department",
					params: { department: department.name },
				},
			};
		}
		if (rights.manageDepartments) {
			return {
				href: "/control-center/departments",
				label: { key: "spaceRestriction.fix.departments" },
			};
		}
		return null;
	};
	if (active.part === "designer") {
		if (active.reason === "server") {
			return rights.manageServer
				? {
						href: "/control-center/license",
						label: { key: "spaceRestriction.fix.license" },
					}
				: null;
		}
		return departmentFix();
	}
	switch (active.reason) {
		case "personal_member_limit":
			return rights.manageMembers
				? {
						href: settingsPath(kind, spaceId, "members"),
						label: { key: "spaceRestriction.fix.members" },
					}
				: null;
		case "personal_spaces_disabled":
			return rights.move
				? {
						href: settingsPath(kind, spaceId, "department"),
						label: { key: "spaceRestriction.fix.move" },
					}
				: null;
		case "department_unlicensed":
			return departmentFix();
		default:
			return null;
	}
}

// ── The member limit of a personal space ──────────────────────────────────────

/**
 * The places of a personal space that are taken: its members, and its pending invitations that have
 * not expired — which hold a place too when somebody is invited (`access::restriction::cap`). What a
 * dialog that adds a member says the limit with, so nobody is told "full" by a refusal first.
 */
export function placesTaken(
	members: number,
	invitations: readonly { status: string; expires_at: string }[],
	now: number = Date.now(),
): number {
	const pending = invitations.filter(
		(i) => i.status === "pending" && new Date(i.expires_at).getTime() >= now,
	).length;
	return members + pending;
}

/**
 * The line a dialog that adds a member to a PERSONAL space says the limit in: the license's limit and
 * the places taken. `null` for a space in a department (it has no limit), and while the limit is not
 * known (no confirmed license, or the session has not loaded).
 */
export function memberLimitHint(
	department: DepartmentRef | null | undefined,
	limit: number | null | undefined,
	taken: number,
): Words | null {
	if (department || typeof limit !== "number") return null;
	return { key: "spaceRestriction.memberLimit.hint", params: { limit, taken } };
}

// ── What a refusal says ───────────────────────────────────────────────────────

interface Coded {
	code?: string;
	details?: unknown;
}

const isNumber = (value: unknown): value is number =>
	typeof value === "number" && Number.isFinite(value);

/**
 * `details` of a `409 personal_member_limit`, or `null` for any other error — or one whose details
 * are not the three numbers (a server that says less than this client expects is not guessed at).
 */
export function memberLimitFromError(
	error: Coded | null | undefined,
): PersonalMemberLimitDetails | null {
	if (error?.code !== "personal_member_limit") return null;
	const d = error.details as Partial<PersonalMemberLimitDetails> | null | undefined;
	if (!d || !isNumber(d.limit) || !isNumber(d.members)) return null;
	return {
		limit: d.limit,
		members: d.members,
		pending: isNumber(d.pending) ? d.pending : 0,
		...(d.kind === "project" || d.kind === "dataspace" ? { kind: d.kind } : {}),
		...(typeof d.id === "string" ? { id: d.id } : {}),
	};
}

/** Who is refused, which says what they can do about it: an admin adding or inviting can make room,
 *  somebody accepting an invitation can only ask. */
export type MemberLimitAudience = "admin" | "invitee";

/** What the refusal of a member to a personal space says, in sentences: the limit, what the space
 *  has, what is pending, and what to do. A move to personal says so — it names a space and has its
 *  own way out; somebody accepting an invitation can only ask for room. */
export function memberLimitWords(
	details: PersonalMemberLimitDetails,
	audience: MemberLimitAudience = "admin",
): Words[] {
	const tail =
		details.kind !== undefined
			? "moveTail"
			: audience === "invitee"
				? "inviteeTail"
				: "addTail";
	return [
		{
			key: "spaceRestriction.errors.memberLimit.limit",
			params: { limit: details.limit },
			plural: details.limit,
		},
		{
			key: "spaceRestriction.errors.memberLimit.has",
			params: { members: details.members },
			plural: details.members,
		},
		...(details.pending > 0
			? [
					{
						key: "spaceRestriction.errors.memberLimit.pending",
						params: { pending: details.pending },
						plural: details.pending,
					},
				]
			: []),
		{ key: `spaceRestriction.errors.memberLimit.${tail}` },
	];
}

/**
 * The words for a refusal that comes of a restriction, by its code and `details.reason` — or `null`
 * for any other error, which the caller words (or shows) as before. For a create dialog that is
 * refused a place the license does not allow:
 *
 *  - `personal_spaces_disabled` (a personal space, and the license allows none);
 *  - `designer_unavailable` (a project, and the Designer is closed there);
 *  - `space_locked` (a department whose license is not valid, a locked space's write).
 *
 * `personal_member_limit` is `memberLimitFromError`'s: its words are sentences with numbers.
 */
export function restrictionRefusalWords(error: Coded | null | undefined): Words | null {
	const code = error?.code;
	if (code === "personal_spaces_disabled") {
		return { key: "spaceRestriction.errors.personal_spaces_disabled" };
	}
	if (code !== "designer_unavailable" && code !== "space_locked") return null;
	const reason = (error?.details as { reason?: unknown } | null | undefined)?.reason;
	const part: RestrictionPart = code === "designer_unavailable" ? "designer" : "locked";
	return {
		key: `spaceRestriction.errors.${code}.${knownReason(part, typeof reason === "string" ? reason : null)}`,
	};
}
